import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth/useLoggedInEmployeeId';
import { useLoggedInUserRoles } from '~/components/Auth/useLoggedInUserRoles';
import { Roles } from '~/data/user';
import { fetchFromOldApi } from '~/services/Http';

export const getSupervisorAccessForEmployee = (
  loggedInEmployeeRole: string,
  loggedInEmployeeId: number | string,
  targetEmployeeId: number | string,
): Promise<number[]> =>
  fetchFromOldApi(
    `/json/auth/role/${loggedInEmployeeRole}/${loggedInEmployeeId}/emp/${targetEmployeeId}`,
  ).then((r) => r.json());

type RoleAccess = {
  [key in Roles]?: boolean;
};

type EmployeeSupervisorRoleAccess = {
  [p: string]: RoleAccess;
};

export const useLoggedInSupervisorAccess = (employeeId: number | string) => {
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const roles = useLoggedInUserRoles();
  const [supervisorAccess, setSupervisorAccess] =
    useState<EmployeeSupervisorRoleAccess>({});

  const userSupervisorRoles: Roles[] = useMemo(() => {
    const allSupervisorRoles = [
      Roles.HR_DEV,
      Roles.HR_ADMIN,
      Roles.HR_ADMIN_LIMITED,
      Roles.HR_SALARY,
      Roles.HR_AUDITOR,
      Roles.DEPUTY_MANAGER,
      Roles.ALT_MANAGER,
      Roles.MANAGER,
    ];

    return allSupervisorRoles.filter((role) => roles.includes(role.toString()));
  }, [roles]);

  useEffect(() => {
    if (!supervisorAccess[`${employeeId}`]) {
      Promise.all(
        userSupervisorRoles.map((role) =>
          getSupervisorAccessForEmployee(
            role.toString(),
            loggedInEmployeeId,
            employeeId,
          ).then((result): RoleAccess => ({ [role]: result })),
        ),
      ).then((results) => {
        setSupervisorAccess({
          ...supervisorAccess,
          [`${employeeId}`]: Object.assign({}, ...results),
        });
      });
    }
  }, [employeeId, loggedInEmployeeId, supervisorAccess, userSupervisorRoles]);

  const isSupervisorWithRole = useCallback(
    (role: Roles) => {
      return supervisorAccess[`${employeeId}`]?.[role] || false;
    },
    [supervisorAccess, employeeId],
  );

  const isSupervisorWithRoles = useCallback(
    (roles: Roles[]) => {
      return !!roles.find((role) => isSupervisorWithRole(role));
    },
    [isSupervisorWithRole],
  );

  return {
    supervisorAccess,
    isSupervisorWithRole,
    isSupervisorWithRoles,
  };
};
